import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
  ReactiveFormsModule,
  FormsModule,
} from '@angular/forms';
import { UsersService } from 'src/app/@pages/users/users.service';
import { IUser } from 'src/app/@interfaces/user.interface';
import { CompanyService } from 'src/app/@pages/company/company.service';
import { ICompany } from 'src/app/@interfaces/company.interface';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { IPrefix } from 'src/app/@interfaces/prefix.interface';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { AlertFeedbackService } from '@shared-services';
import { GraphQLErrorResponse } from '@shared-models';
import { TranslatePipe } from '@ngx-translate/core';
import { MatOptionModule } from '@angular/material/core';
import { MatButtonModule } from '@angular/material/button';

import { MatFormFieldModule } from '@angular/material/form-field';
import { MatDialogModule } from '@angular/material/dialog';
import { STRING_CONSTANTS } from '@shared-constants';

@Component({
  selector: 'app-add-company',
  templateUrl: './add-company.component.html',
  styleUrls: ['./add-company.component.css'],
  imports: [
    MatDialogModule,
    ReactiveFormsModule,
    FormsModule,
    MatFormFieldModule,
    MatButtonModule,
    MatSelectModule,
    MatOptionModule,
    TranslatePipe,
  ],
})
export class AddCompany implements OnInit, OnDestroy {
  deliveryTxt: string = '';
  @Input() id_user: number = 0;
  @Input() valuesForm: any = '';
  userModify = {} as IUser;
  companyList: Array<ICompany> = [];
  prefixList: IPrefix[] = [];
  companyForm: UntypedFormGroup = this.fb.group({
    companie: ['', Validators.required],
    prefix: [''],
  });
  unsubscribe$ = new Subject<void>();
  constructor(
    private userS: UsersService,
    private companyS: CompanyService,
    private fb: UntypedFormBuilder,
    private alertFeedback: AlertFeedbackService,
  ) {}

  ngOnInit() {
    this.loadUser();

    this.companyS
      .getAllCompanies()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((result) => {
        this.companyList = result;
      });
  }

  // Reloads the user so the companies table in this dialog reflects the latest
  // assignment/removal without needing to reopen the dialog.
  private loadUser() {
    this.userS
      .getUser(this.id_user)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((result) => {
        this.userModify = result;
      });
  }

  onOptionSelected(event: MatSelectChange) {
    const value = event.value;
    if (
      value.internal_electronic_invoicing === STRING_CONSTANTS.TRUE &&
      value.countries[0].name === 'Colombia'
    ) {
      this.companyForm.get('prefix')?.setValidators(Validators.required);
      this.companyForm.get('prefix')?.updateValueAndValidity();
      this.prefixList = value.prefixes;
    } else {
      this.companyForm.get('prefix')?.clearValidators();
      this.companyForm.get('prefix')?.updateValueAndValidity();
      this.prefixList = [];
      this.companyForm.get('prefix')?.setValue(value.prefixes[0]);
    }
  }

  update() {
    this.userS
      .addCompanies(
        parseInt(this.id_user.toString()),
        parseInt(this.companyForm.value.companie.Id_company),
        parseInt(this.companyForm.value.prefix.id),
      )
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (result) => {
          if (result) {
            // Seller registration (user_seller) is handled by the backend based
            // on the persisted role + full company set — no order dependency and
            // it also covers preexisting companies. See ticket 86ajc92gq.
            this.companyForm.reset();
            this.prefixList = [];
            this.loadUser();
            this.alertFeedback.success('users.companyAdded');
          } else {
            this.alertFeedback.error('users.somethingWrong');
          }
        },
        error: (err: GraphQLErrorResponse) =>
          this.alertFeedback.errorFromGraphQL(err),
      });
  }

  remove(id_user: number, id_company: number) {
    this.userS
      .removeCompanies(
        parseInt(id_user.toString()),
        parseInt(id_company.toString()),
      )
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        // The removeCompany mutation returns null on success, so a truthy
        // result means the deletion did not go through (error).
        next: (result) => {
          if (result) {
            this.alertFeedback.error('users.somethingWrong');
          } else {
            this.loadUser();
            this.alertFeedback.success('users.companyRemoved');
          }
        },
        error: (err: GraphQLErrorResponse) =>
          this.alertFeedback.errorFromGraphQL(err),
      });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
